import { Link } from "react-router-dom";
import { ArrowRight, Mic, CheckCircle2, Calendar, Users, Sparkles } from "lucide-react";
import { SiteNav } from "@/components/site/SiteNav";
import { SiteFooter } from "@/components/site/SiteFooter";
import { PageMeta } from "@/components/site/PageMeta";
import { FAQSection } from "@/components/site/FAQSection";
import { Reveal } from "@/hooks/useReveal";
import { EMAIL, PHONE_DISPLAY, PHONE_HREF } from "@/content/site";
import { buildFAQPageJsonLd, type FAQItem } from "@/content/faq";
import { authorRef, organizationNode, publisherRef } from "@/lib/entity-jsonld";

/**
 * Speaking FAQ. Rendered by <FAQSection> AND marked up as FAQPage from the same
 * array, so Google's "answers must be visible" rule can never be violated.
 * Answers stay factual — fees, formats, travel, and turnaround only.
 */
const SPEAKING_FAQ: FAQItem[] = [
  {
    q: "How do I book Dr. Sully to speak at my event?",
    a: "Submit a booking request at /inquire?lane=speaking or call (855) 76-COACH with your date, city, audience, and format. The booking team responds within one business day and sends a fee sheet on request.",
  },
  {
    q: "What does a keynote cost?",
    a: "Keynote engagements start at $15K and are quoted from there based on travel, audience size, format, and any pre-work or breakout sessions. Nonprofit, church, and school rates are considered case by case.",
  },
  {
    q: "What formats are available?",
    a: "Keynotes of 45 to 75 minutes with Q&A, half-day or full-day workshops, moderated panels and fireside conversations, and multi-day residencies for conferences and retreats.",
  },
  {
    q: "Does Dr. Sully travel, and can he present virtually?",
    a: "Yes. He is based in San Antonio, Texas and travels worldwide for engagements, and he also delivers keynotes and workshops virtually for distributed teams and online conferences.",
  },
  {
    q: "What topics does he speak on?",
    a: "Identity and decision velocity, trauma-informed leadership, equity and resilience in the workplace, personal equity for athletes and founders, the Honest Kingdom Voice for faith audiences, and AI, faith, and the future of work.",
  },
  {
    q: "What audiences does he work with?",
    a: "Corporate and executive teams, universities and schools, government and civic bodies, churches and faith conferences, athletic programs, and nonprofits and foundations.",
  },
];


const TOPICS = [
  {
    title: "If It Doesn't Make Sense — Make Change",
    desc: "Signature keynote on identity, decision velocity, and leadership clarity.",
  },
  {
    title: "Trauma-Informed Leadership",
    desc: "Building organizations that perform without breaking the people inside them.",
  },
  {
    title: "Equity & Resilience in the Workplace",
    desc: "Practical frameworks for culture, belonging, and outcomes that hold up to scrutiny.",
  },
  {
    title: "Identity, Purpose & Personal Equity",
    desc: "For athletes, founders, and emerging leaders navigating high-stakes seasons.",
  },
  {
    title: "The Honest Kingdom Voice",
    desc: "For faith audiences: apostolic clarity, biblical truth, prophetic precision.",
  },
  {
    title: "AI, Faith & the Future of Work",
    desc: "Human-first AI adoption for ministries, schools, and mission-driven orgs.",
  },
];

const AUDIENCES = [
  { icon: Users, label: "Corporate & Executive Teams" },
  { icon: Users, label: "Universities & Schools" },
  { icon: Users, label: "Government & Civic Bodies" },
  { icon: Users, label: "Churches & Faith Conferences" },
  { icon: Users, label: "Athletic Programs & Locker Rooms" },
  { icon: Users, label: "Nonprofits & Foundations" },
];

const FORMATS = [
  { title: "Keynote", desc: "45–75 min mainstage talk with Q&A." },
  { title: "Workshop", desc: "Half-day or full-day interactive session." },
  { title: "Panel & Fireside", desc: "Moderated conversations and panels." },
  { title: "Multi-Day Residency", desc: "Custom engagements for conferences and retreats." },
];

const Speaking = () => {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <PageMeta
        title="Book Dr. Sully to Speak — Keynote Speaker"
        description="Book Dr. Sully — keynote speaker for corporate, university, government, and faith audiences. Trauma-informed leadership, equity, identity, AI & the kingdom voice."
        path="/speaking"
        jsonLd={{
          "@context": "https://schema.org",
          // One connected graph: the Service is provided by the canonical
          // Person/Organization entities, and the FAQ answers are credited to
          // the same brand instead of floating unattached.
          "@graph": [
            organizationNode(),
            {
              "@type": "Service",
              "@id": "https://drsullyspeaks.com/speaking#service",
              serviceType: "Keynote Speaking",
              name: "Dr. Sully — Keynote Speaking",
              provider: authorRef,
              brand: publisherRef,
              areaServed: "Worldwide",
              url: "https://drsullyspeaks.com/speaking",
              description:
                "Keynotes, workshops, panels, and multi-day residencies for corporate, university, government, athletic, nonprofit, and faith audiences.",
              offers: {
                "@type": "Offer",
                availability: "https://schema.org/InStock",
                url: "https://drsullyspeaks.com/inquire?lane=speaking",
              },
            },
            {
              ...buildFAQPageJsonLd(SPEAKING_FAQ, "https://drsullyspeaks.com/speaking#faq"),
              publisher: publisherRef,
            },
          ],
        }}

      />
      <SiteNav />

      {/* HERO */}
      <section className="relative pt-32 md:pt-40 pb-20 bg-hero overflow-hidden">
        <div className="container max-w-4xl text-center">
          <Reveal variant="up">
            <span className="eyebrow !mx-auto"><Mic className="inline h-3 w-3 mr-2" />Now booking · 2026 keynote dates open</span>
            <h1 className="font-display font-bold uppercase text-white text-4xl md:text-6xl lg:text-7xl mt-4 leading-[1.05]">
              A keynote your room<br />
              <span className="text-gradient-gold">won't forget.</span>
            </h1>
            <p className="text-silver text-base md:text-lg mt-6 max-w-2xl mx-auto leading-relaxed">
              Academic authority. Street-level honesty. Prophetic precision.
              Book Dr. Sully to close your event with a standing ovation — and leaders who actually change on Monday.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/inquire?lane=speaking" className="inline-flex items-center justify-center gap-2 bg-cta text-white font-display uppercase tracking-[0.2em] text-xs px-7 py-5 shadow-cta hover:brightness-110 transition-all">
                Check My Date <ArrowRight className="h-4 w-4" />
              </Link>
              <a href={`mailto:${EMAIL}?subject=Speaking%20Inquiry`} className="inline-flex items-center justify-center gap-2 border border-gold/50 text-white font-display uppercase tracking-[0.2em] text-xs px-7 py-5 hover:bg-gold/10 transition-all">
                Email the Booking Team
              </a>
            </div>
            <p className="mt-4 text-[11px] uppercase tracking-[0.2em] text-silver/70">
              Replies within 1 business day · Fee sheet on request
            </p>

          </Reveal>
        </div>
      </section>

      {/* SIGNATURE TOPICS */}
      <section className="py-24">
        <div className="container max-w-5xl">
          <Reveal>
            <div className="text-center mb-12">
              <span className="eyebrow !mx-auto"><Sparkles className="inline h-3 w-3 mr-2" />Signature Topics</span>
              <h2 className="font-display uppercase text-white text-3xl md:text-4xl mt-3">
                What he <span className="text-gradient-gold">speaks on</span>.
              </h2>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-px bg-border border border-border">
            {TOPICS.map((t) => (
              <div key={t.title} className="bg-navy p-6 hover:bg-navy-elevated transition-colors">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-gold shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-display uppercase text-white text-base tracking-wide">{t.title}</h3>
                    <p className="text-silver text-sm mt-2 leading-relaxed">{t.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AUDIENCES */}
      <section className="py-20 bg-navy-deep border-y border-border">
        <div className="container max-w-5xl">
          <Reveal>
            <div className="text-center mb-12">
              <span className="eyebrow !mx-auto">Audiences</span>
              <h2 className="font-display uppercase text-white text-3xl md:text-4xl mt-3">
                Rooms he's <span className="text-gradient-gold">moved</span>.
              </h2>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border">
            {AUDIENCES.map((a) => (
              <div key={a.label} className="bg-navy p-5 flex items-center gap-3">
                <a.icon className="h-4 w-4 text-gold shrink-0" />
                <span className="text-white text-sm">{a.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FORMATS */}
      <section className="py-24">
        <div className="container max-w-5xl">
          <Reveal>
            <div className="text-center mb-12">
              <span className="eyebrow !mx-auto"><Calendar className="inline h-3 w-3 mr-2" />Formats</span>
              <h2 className="font-display uppercase text-white text-3xl md:text-4xl mt-3">
                How he <span className="text-gradient-gold">shows up</span>.
              </h2>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border">
            {FORMATS.map((f) => (
              <div key={f.title} className="bg-navy p-6">
                <h3 className="font-display uppercase text-gold text-sm tracking-[0.2em]">{f.title}</h3>
                <p className="text-silver text-sm mt-3 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ — mirrored 1:1 by the FAQPage JSON-LD above */}
      <FAQSection
        items={SPEAKING_FAQ}
        slugPrefix="speaking-faq"
        eyebrow="Booking · Frequently Asked"
        heading="Booking questions, answered."
        subheading="Fees, formats, travel, and turnaround — before you send the request."
      />

      {/* CTA */}

      <section className="pb-32">
        <div className="container max-w-3xl">
          <div className="premium-card p-8 md:p-12 text-center">
            <h2 className="font-display uppercase text-white text-2xl md:text-3xl">
              Let's get on <span className="text-gradient-gold">your stage</span>.
            </h2>
            <p className="text-silver text-sm md:text-base mt-4 max-w-xl mx-auto">
              Share your event details — date, audience, format, and any
              theme. The booking team responds within 48 hours.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/inquire?lane=speaking" className="inline-flex items-center justify-center gap-2 bg-cta text-white font-display uppercase tracking-[0.2em] text-xs px-7 py-5 shadow-cta hover:brightness-110 transition-all">
                Submit Booking Request <ArrowRight className="h-4 w-4" />
              </Link>
              <a href={PHONE_HREF} className="inline-flex items-center justify-center gap-2 border border-gold/50 text-white font-display uppercase tracking-[0.2em] text-xs px-7 py-5 hover:bg-gold/10 transition-all">
                Call {PHONE_DISPLAY}
              </a>
            </div>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default Speaking;
